/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
  SerializedLexicalNode,
  Spread
} from 'lexical';
import {
  $applyNodeReplacement,
  DecoratorNode
} from 'lexical';
import { Suspense, useRef, useState, type JSX } from 'react';

import { TAssetMetadata } from '@dl/declarations';
import brokenImage from '../images/image-broken.svg';
import { AssetMetadataDialog } from '../shared/AssetMenu';
import { EditableNodeWrapper } from './CustomEmbedNode';

export function ImageComponent({
  src,
  headline,
  credit,
  copyright
}: {
  src: string;
  alt_text: string;
  headline: string;
  credit?: string;
  copyright?: string;
  article_caption?: string;
  link?: string;
  nodeKey: NodeKey;
  width: 'inherit' | number;
  height: 'inherit' | number;
}): JSX.Element {

  const imageRef = useRef<null | HTMLImageElement>(null);
  const [isLoadError, setIsLoadError] = useState<boolean>(false);

  return (
    <Suspense fallback={null}>
      <>
        {isLoadError ? (
          <img
            src={brokenImage}
            style={{
              height: 200,
              opacity: 0.2,
              width: 200,
            }}
            draggable="false"
          />
        ) : (
          <figure
            className="border rounded-md overflow-hidden"
          >
            <img
              src={src}
              alt={headline}
              ref={imageRef}
              draggable="false"
              onError={() => setIsLoadError(true)}
            />
            <figcaption
              className="px-2 pb-2 not-italic!"
            >
              <span className="font-semibold text-red-500">
                Internal note: Please do not forget to review the caption.
              </span>

              <br />

              <span className="font-semibold text-sm">
                {headline}
              </span>

              <br />

              {copyright && <span className="text-sm">Illustration: {copyright}; </span>}
              {credit && <span className="text-sm">Credit: {credit} </span>}

            </figcaption>
          </figure>
        )}
      </>
    </Suspense>
  );
}
export interface ImagePayload extends Pick<TAssetMetadata, "headline" | "link" | "copyright" | "credit" | "article_caption"> {
  src: string;
  alt_text: string;
  width?: number;
  height?: number;
  key?: NodeKey;
}

function $convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img.src.startsWith('file:///')) {
    return null;
  }
  const { alt: alt_text, src, width, height } = img;

  const node = $createImageNode({ src, alt_text, headline: alt_text, credit: '', copyright: '', article_caption: alt_text, height, width });

  return { node };
}

export type SerializedImageNode = Spread<
  Omit<ImagePayload, 'key'>,
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __alt_text: string;
  __headline: string;
  __credit: string;
  __copyright: string;
  __article_caption: string;
  __link: string;
  __width: 'inherit' | number;
  __height: 'inherit' | number;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__alt_text,
      node.__headline,
      node.__credit,
      node.__copyright,
      node.__article_caption,
      node.__link,
      node.__width,
      node.__height,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const {
      src,
      alt_text,
      headline,
      credit,
      copyright,
      article_caption,
      link,
      height,
      width,
    } = serializedNode;

    return $createImageNode({
      src,
      alt_text,
      headline,
      credit,
      copyright,
      article_caption,
      link,
      height,
      width,
    }).updateFromJSON(serializedNode);
  }

  updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedImageNode>): this {
    const node = super.updateFromJSON(serializedNode);
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');

    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__alt_text);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (_node: Node) => ({
        conversion: $convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    alt_text: string,
    headline: string,
    credit?: string,
    copyright?: string,
    article_caption?: string,
    link?: string,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__alt_text = alt_text;
    this.__headline = headline || '';
    this.__credit = credit || '';
    this.__copyright = copyright || '';
    this.__article_caption = article_caption || '';
    this.__link = link || '';
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      src: this.__src,
      alt_text: this.__alt_text,
      headline: this.__headline,
      credit: this.__credit,
      copyright: this.__copyright,
      article_caption: this.__article_caption,
      link: this.__link,
      width: this.__width === 'inherit' ? 0 : this.__width,
      height: this.__height === 'inherit' ? 0 : this.__height,
    };
  }

  setWidthAndHeight(
    width: 'inherit' | number,
    height: 'inherit' | number,
  ): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    const theme = config.theme;
    const className = theme.image;

    if (className !== undefined) {
      div.className = className;
    }

    return div;
  }

  updateDOM(): false {
    return false;
  }

  decorate(editor: LexicalEditor): JSX.Element {
    return (
      <EditableNodeWrapper
        title='Edit Metadata'
        wrapperClassName='w-2/3'
        nodeKey={this.getKey()}
        Component={(
          <ImageComponent
            src={this.__src}
            alt_text={this.__alt_text}
            headline={this.__headline}
            credit={this.__credit}
            copyright={this.__copyright}
            article_caption={this.__article_caption}
            link={this.__link}
            width={this.__width}
            height={this.__height}
            nodeKey={this.getKey()}
          />
        )}
        EditComponent={(
          <AssetMetadataDialog
            defaultValues={{
              _id: '',
              src_url: this.__src,
              link: this.__link,
              credit: this.__credit,
              copyright: this.__copyright,
              headline: this.__headline,
              article_caption: this.__article_caption,
            }}
            handleSaveChanges={(data: TAssetMetadata) => {
              editor.update(() => {
                const writable = this.getWritable();
                writable.__headline = data.headline;
                writable.__alt_text = data.headline;
                writable.__credit = data.credit;
                writable.__copyright = data.copyright;
                writable.__link = data.link || '';
                writable.__article_caption = data.article_caption;
              });
            }}
          />
        )}
        dialogOptions={{
          editComponentMaxWidth: 'sm'
        }}
      />
    )
  }
}

export function $createImageNode(payload: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(
      payload.src,
      payload.alt_text,
      payload.headline,
      payload.credit,
      payload.copyright,
      payload.article_caption,
      payload.link,
      payload.width,
      payload.height,
      payload.key,
    ),
  );
}

export function $isImageNode(
  node: LexicalNode | null | undefined,
): node is ImageNode {
  return node instanceof ImageNode;
}
